import {isArray, isEqual} from 'lodash-es';

import {EDefaultValueRuleType} from 'components/form/hooks/field-default-value.hook';
import {EFieldType, IFieldMeta, IFormEntityMeta} from 'entities/metadata';
import {IEntityData, TEntity} from 'modules/data/data-types';
import {normalizeFormDataForRequest} from 'modules/data/utils/data.utils';
import {stripHtmlSafe} from 'shared/utils';
import {store} from 'store/config';
import {getContextDataValueByRule, normalizeBoolValue} from 'utils/common';

type TValidateReferenceFirstOptionArgs = {
    fieldMeta: IFieldMeta;
    initialData?: TEntity;
    currentData?: TEntity;
    referenceData?: TEntity[];
}

const isReferenceFirstOptionDirty = ({
    fieldMeta,
    initialData,
    currentData,
    referenceData = [],
}:TValidateReferenceFirstOptionArgs) => {
    if (fieldMeta?.additionalInputOptions) {
        const defaultValue = fieldMeta.additionalInputOptions[0]?.value;
        if (!initialData && currentData && currentData?.value !== defaultValue) {
            return true;
        }
        if (initialData && initialData?.id !== currentData?.id) {
            return true;
        }
        return false;
    }

    if (!initialData && currentData && currentData?.id !== referenceData[0].id) {
        return true;
    }

    if (initialData && !initialData?.id) {
        if (initialData && fieldMeta.additionalInputOptions
            && currentData?.id !== initialData) {
            return true;
        }
        if (initialData && currentData?.id && currentData?.id !== initialData) {
            return true;
        }
        if (initialData && !currentData?.id && currentData !== initialData) {
            return true;
        }
        return false;
    }

    if (initialData?.id && currentData?.id && currentData?.id !== initialData?.id) {
        return true;
    }
    return false;
}

/**
 * Проверяет, нет ли изменений в данных формы
 * @param entityName - Имя сущности, у которой нужно проверить изменения данных у формы
 * @param currentData - Текущие данные формы (опционально)
 * @param initialData - Первоначальные данные формы
 * @param metadata - Мета формы (опционально)
 */
export const isFormDirty = (
    entityName: string,
    currentData?: Record<string, any>,
    initialData?: Record<string, any>,
    metadata?: IFormEntityMeta,
) => {
    if (!currentData) {
        return false;
    }
    let isDirty = false;

    if (!metadata) {
        return Object.keys(currentData).some(key => currentData[key] !== initialData?.[key]);
    }
    const normalizedInitialData = !initialData
        ? Object.fromEntries(metadata.fields.map(field => [field.key, undefined]))
        : initialData;


    const normalizedData = normalizeFormDataForRequest(currentData, metadata?.fields || []);

    Object.keys(normalizedData).forEach(key => {
        const fieldMeta = metadata?.fields.find(field => field.key === key)
            || metadata.fields.map(field => {
                if (field?.children) {
                    return field?.children.find(child => child.key === key);
                }
                return undefined;
            }).filter(el => el)[0];
        const keyWithoutId = key.slice(0, -2);
        const fieldMetaWithoutId = metadata?.fields.find(v => v.key === keyWithoutId);

        if (fieldMeta && fieldMeta.isHidden) {
            return;
        }

        if (!Object.keys(normalizedInitialData).includes(key)) {
            // it could be reference value, that is normalized with id
            if (!fieldMeta && key.endsWith('Id')) {
                // it's a reference and its default value is saved without id
                if (fieldMetaWithoutId && fieldMetaWithoutId?.type === EFieldType.REFERENCE) {
                    if (Object.keys(normalizedInitialData).includes(keyWithoutId)) {
                        if (isArray(currentData[keyWithoutId])
                            && currentData[keyWithoutId] !== normalizedInitialData[keyWithoutId]) {
                            isDirty = true;
                        }
                        if (fieldMetaWithoutId.multipleMode
                            && JSON.stringify(normalizedInitialData[keyWithoutId]) !== JSON.stringify(currentData[key])) {
                            isDirty = true;
                            return;
                        }
                        if (fieldMetaWithoutId?.useFirstOptionAsDefaultValue && fieldMetaWithoutId.referenceUrl) {
                            const refData = store.getState().DATA_MODULE[
                                fieldMetaWithoutId.referenceUrl].REFERENCE.rows;
                            if (isReferenceFirstOptionDirty({
                                fieldMeta: fieldMetaWithoutId,
                                currentData: currentData[keyWithoutId],
                                initialData: normalizedInitialData[keyWithoutId],
                                referenceData: refData,
                            })) isDirty = true;
                            return;
                        }
                        if (fieldMetaWithoutId.defaultValueByRule) {
                            if (fieldMetaWithoutId.defaultValueByRule === EDefaultValueRuleType.USER_TAX_PERIOD) {
                                const contextData = getContextDataValueByRule(
                                    fieldMetaWithoutId.defaultValueByRule,
                                    store.getState().CONTEXT_MODULE.rawData,
                                );
                                if (currentData[keyWithoutId].id !== contextData) isDirty = true;
                            }
                            return;
                        }
                        // reference value are stored as objects, so we need to check their id equality
                        if (currentData[keyWithoutId]?.id !== normalizedInitialData[keyWithoutId]?.id) isDirty = true;
                        return;
                    }
                    if (currentData[keyWithoutId]?.id) isDirty = true;
                }
                return;
            }
            if (currentData[key] && fieldMetaWithoutId && !fieldMetaWithoutId.defaultValueByRule) isDirty = true;
            if (!fieldMeta && (Array.isArray(currentData[key]) ? currentData[key].length > 0 : currentData[key])) {
                isDirty = true;
            }
            return;
        }

        if (Object.keys(normalizedInitialData).includes(key) && Object.keys(normalizedInitialData).includes(keyWithoutId)
            && fieldMetaWithoutId?.type === EFieldType.REFERENCE) {
            if (isArray(normalizedInitialData[keyWithoutId])) {
                const initialDataIds = (normalizedInitialData[keyWithoutId] as Array<any>).map(elem => elem.id).sort();
                const currentDataIds = (currentData[keyWithoutId] as Array<any>).map(elem => elem.id).sort();

                if (initialDataIds.length !== currentDataIds.length) {
                    isDirty = true;
                }

                for (let i = 0; i < initialDataIds.length; i += 1) {
                    if (initialDataIds[i] !== currentDataIds[i]) {
                        isDirty = true;
                    }
                }
            }

            if (typeof normalizedInitialData[keyWithoutId] === 'object'
                ? currentData[keyWithoutId].id !== normalizedInitialData[keyWithoutId].id
                : currentData[keyWithoutId].id !== normalizedInitialData[keyWithoutId]) {
                isDirty = true;
            }
            return;
        }

        // reference value are stored as objects, so we need to check their id equality
        if (fieldMeta && fieldMeta?.type === EFieldType.REFERENCE) {
            if (fieldMeta.defaultValueByRule) {
                if (fieldMeta.defaultValueByRule === EDefaultValueRuleType.DEFAULT_LOOK_UP_VALUE && !normalizedInitialData[key]) {
                    // attribute1 - дефолтное значение для поля monitorProgramId в directories.type.documents
                    if (!normalizedInitialData[key] && currentData[key].attribute1 !== 'Y') isDirty = true;
                    if (normalizedInitialData[key] && currentData[key].id !== normalizedInitialData[key]) isDirty = true;
                }
                if ((fieldMeta.defaultValueByRule === EDefaultValueRuleType.USER_ORGANIZATION
                    || fieldMeta.defaultValueByRule === EDefaultValueRuleType.USER_TAX_PERIOD) && !normalizedInitialData[key]) {
                    const contextData = getContextDataValueByRule(
                        fieldMeta.defaultValueByRule,
                        store.getState().CONTEXT_MODULE.rawData,
                    );
                    if (currentData[key].id !== contextData) isDirty = true;
                }
                if (normalizedInitialData[key] && currentData[key].id !== normalizedInitialData[key]) isDirty = true;
                return;
            }

            if (fieldMeta.defaultValue) {
                if (fieldMeta.additionalInputOptions && fieldMeta.additionalInputOptions.filter(
                    inputOption => fieldMeta.defaultValue === inputOption.key,
                ) && normalizedInitialData[key] === currentData[key]) {
                    return;
                }
                if (typeof normalizedInitialData[key] === 'string' && currentData[key].lookupCode !== normalizedInitialData[key]) {
                    isDirty = true;
                }
                if (typeof normalizedInitialData[key] === 'number' && currentData[key].id !== normalizedInitialData[key]) {
                    isDirty = true;
                }
                return;
            }

            if (isArray(currentData[key]) && currentData[key] !== normalizedInitialData[key]) {
                isDirty = true;
            }

            if (fieldMeta.useFirstOptionAsDefaultValue && fieldMeta.referenceUrl) {
                let referenceDataWithFilterParams: TEntity[] | undefined;
                if (fieldMeta?.isDependsOnMainFilter === 'Y') {
                    const filterData = store.getState().DATA_MODULE[entityName]?.FILTER?.data as IEntityData;
                    const filterId = filterData && Object.values(filterData).length && Object.values(filterData)[0]?.id;
                    const referenceDataArr = Object.keys(store.getState().DATA_MODULE).map(
                        refKey => [refKey, store.getState().DATA_MODULE[refKey]],
                    ).sort();
                    const referenceDataArrFiltered = referenceDataArr.find(
                        e => {
                            const searchParams = new URLSearchParams(e[0].replace(fieldMeta?.referenceUrl, ''));
                            return searchParams.get('sublistId') === String(filterId)
                                && searchParams.get('sublistCode') === String(currentData[key].id);
                        },
                    );

                    if (Array.isArray(referenceDataArrFiltered) && referenceDataArrFiltered.length === 2) {
                        referenceDataWithFilterParams = referenceDataArrFiltered[1]?.REFERENCE?.rows;
                    }
                }
                const referenceData = referenceDataWithFilterParams
                    || store.getState().DATA_MODULE[fieldMeta.referenceUrl].REFERENCE.rows;
                if (isReferenceFirstOptionDirty({
                    fieldMeta,
                    referenceData,
                    initialData: normalizedInitialData[key],
                    currentData: currentData[key],
                })) isDirty = true;

                return;
            }

            if (normalizedInitialData[key]?.id) {
                if (currentData[key]?.id !== normalizedInitialData[key]?.id) isDirty = true;
                return;
            }
            if (currentData[key]?.id) {
                if (currentData[key]?.id !== normalizedInitialData[key]) isDirty = true;
                return;
            }
            if (currentData[key] !== normalizedInitialData[key]) isDirty = true;
            return;
        }

        if (fieldMeta && fieldMeta?.type === EFieldType.STATIC_SELECT) {
            if (!normalizedInitialData[key] && fieldMeta.defaultValue !== currentData[key]) isDirty = true;
            if (normalizedInitialData[key] && currentData[key] !== normalizedInitialData[key]) isDirty = true;
            return;
        }

        if (fieldMeta && fieldMeta?.type === EFieldType.COMBINED_FIELD_WITH_DELETE && fieldMeta.children) {
            const isDataEmpty = currentData[key] ? currentData[key].some(
                (row: {[index: string]: string}) => !fieldMeta?.children?.some(field => row[field.key]),
            ) : true;
            if (!normalizedInitialData[key]) {
                if (!isDataEmpty) isDirty = true;
                return;
            }

            const errorFieldsMap: IFieldMeta[] = fieldMeta?.children.reduce((acc: IFieldMeta[], childMeta: IFieldMeta) => {
                const initial = normalizedInitialData[key]?.[0]?.[childMeta.key];
                const current = currentData[key]?.[0]?.[childMeta.key];

                if (childMeta?.useFirstOptionAsDefaultValue && childMeta?.additionalInputOptions) {
                    if (isReferenceFirstOptionDirty({
                        fieldMeta: childMeta,
                        initialData: initial,
                        currentData: current,
                    })) acc.push(childMeta);
                }
                if (JSON.stringify(initial) !== JSON.stringify(current)) return acc;
                return acc;
            }, []);
            if (errorFieldsMap?.length) isDirty = true;
            return;
        }

        if (fieldMeta && (fieldMeta.type === EFieldType.EXTERNAL_EMPLOYEE_BOOL
            || fieldMeta.type === EFieldType.BOOLEAN)) {
            if (normalizeBoolValue(currentData[key]) !== normalizeBoolValue(normalizedInitialData[key])) isDirty = true;
            return;
        }

        if (fieldMeta && (fieldMeta?.type === EFieldType.DATE || fieldMeta?.type === EFieldType.TIME_WITH_LABEL_INSIDE)) {
            if (!currentData[key] && !normalizedInitialData[key]) return;
            if (currentData[key] !== normalizedInitialData[key]) isDirty = true;
            return;
        }

        if (fieldMeta && fieldMeta?.type === EFieldType.FIELDS_TREE) {
            currentData[key].forEach((program: any, index: number) => {
                if (normalizedInitialData[key] && normalizedInitialData[key][index]) {
                    if (program.program.id && normalizedInitialData[key][index].program) {
                        if (program.program.id !== normalizedInitialData[key][index].program.id) {
                            isDirty = true;
                        }
                    }
                    return;
                }
                if (program) isDirty = true;
            });
            return;
        }

        if (fieldMeta && fieldMeta?.type === EFieldType.RICH_TEXT) {
            if (currentData[key] === normalizedInitialData[key]) {
                return;
            }
            // filter html entities
            const currentDataHtml = document.createElement('textarea');
            const initialDataHtml = document.createElement('textarea');
            currentDataHtml.innerHTML = currentData[key] || '';
            initialDataHtml.innerHTML = normalizedInitialData[key] || '';
            // filter html tags
            if (stripHtmlSafe(currentDataHtml.value) !== stripHtmlSafe(initialDataHtml.value)) {
                isDirty = true;
            }
            return;
        }

        const initialFormData = store.getState().DATA_MODULE[entityName].FORM.initialData;

        if (metadata.additionalOptions ? !isEqual(String(normalizedData[key]), String(initialFormData[key]))
            : !isEqual(String(normalizedData[key]), String(normalizedInitialData[key]))) {
            isDirty = true;
        }
    });

    return isDirty;
};
